/**
 * Doughnut chart component for proportional analysis with center space
 */

import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import BaseChart from './BaseChart';
import { ChartTheme, getChartColors } from '../../utils/chartConfig';

// Register Chart.js components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

interface DoughnutChartProps {
  data: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor?: string | string[];
      borderColor?: string | string[];
      borderWidth?: number;
    }>;
  };
  theme: ChartTheme;
  loading?: boolean;
  error?: string | null;
  height?: number;
  className?: string;
  title?: string;
  showLegend?: boolean;
  showPercentage?: boolean;
  cutout?: number;
  centerText?: string;
  centerValue?: string | number;
}

const DoughnutChart: React.FC<DoughnutChartProps> = ({
  data,
  theme,
  loading = false,
  error = null,
  height = 300,
  className = '',
  title,
  showLegend = true,
  showPercentage = true,
  cutout = 60,
  centerText,
  centerValue
}) => {
  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: title ? {
        display: true,
        text: title,
        color: theme.text,
        font: {
          family: 'Inter, system-ui, sans-serif',
          size: 16,
          weight: 'bold' as const
        }
      } : undefined,
      legend: {
        display: showLegend,
        position: 'right' as const,
        labels: {
          color: theme.text,
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 12
          },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: theme.background,
        titleColor: theme.text,
        bodyColor: theme.textSecondary,
        borderColor: theme.border,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        titleFont: {
          family: 'Inter, system-ui, sans-serif',
          size: 13,
          weight: 'bold' as const
        },
        bodyFont: {
          family: 'Inter, system-ui, sans-serif',
          size: 12
        },
        callbacks: {
          label: (context: any) => {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return showPercentage 
              ? `${label}: ${value} (${percentage}%)`
              : `${label}: ${value}`;
          }
        }
      }
    },
    cutout: cutout
  };

  // Process data to add default colors if not provided
  const processedData = {
    ...data,
    datasets: data.datasets.map((dataset) => ({
      ...dataset,
      backgroundColor: dataset.backgroundColor || getChartColors(theme, data.labels.length),
      borderColor: dataset.borderColor || theme.background,
      borderWidth: dataset.borderWidth || 2
    }))
  };

  return (
    <BaseChart
      data={processedData}
      options={options}
      theme={theme}
      loading={loading}
      error={error}
      height={height}
      className={className}
    >
      {({ data, options, height }) => (
        <div className="relative">
          <Doughnut data={data} options={options} />
          {(centerText || centerValue) && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                {centerValue && (
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {centerValue}
                  </div>
                )}
                {centerText && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {centerText}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </BaseChart>
  );
};

export default DoughnutChart;
